// Waiting out a provider rate limit instead of failing the order.
//
// DoorDash limits the whole MealOps account, and says for how long ("retry
// after N seconds"). Our API answers such a refusal with a coded 429
// (PROVIDER_RATE_LIMITED + retryAfterSeconds): nothing was placed, nothing was
// held, and the same request is safe to send again once the window reopens.
// Failing instead is what left a three-way split two-thirds placed
// (2026-09-19): the third child's re-price landed 3 seconds into a 48-second
// window and the split simply stopped. A slower order beats a partial one.
//
// Rules the limiter forces on us:
//   - never retry EARLY: every call into a closed window lengthens it, so the
//     wait is the advertised retry-after plus a pad, never less;
//   - one attempt per window — no bursts of retries;
//   - give up only after a long budget, and only by handing the original
//     error back to the caller's normal failure path.

export const PROVIDER_RATE_LIMITED_CODE = 'PROVIDER_RATE_LIMITED';

const DEFAULT_RETRY_AFTER_SECONDS = 60;
const RETRY_PAD_MS = 3_000;
const MIN_WAIT_MS = 5_000;
// Total time one order may spend waiting. DoorDash's windows have run from a
// few seconds to ~10 minutes (594 s on a coach's own account, 2026-10-19);
// this covers several in a row.
export const DEFAULT_PROVIDER_WAIT_BUDGET_MS = 20 * 60_000;
const HEARTBEAT_EVERY_MS = 60_000;

const toSeconds = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// The figure Phantom writes into its coded message ("… [retry_after_seconds=12]",
// or dd-cli's "Retry after 12 seconds"). A body passed through as is — the
// quote route's 422 — carries the code but no structured figure, and the
// fixed default used to win over it: a 12-second window waited 63 seconds
// and a 284-second one was re-quoted early (2026-10-07).
const messageRetryAfterSeconds = (...texts) => {
  const text = texts.filter((part) => typeof part === 'string').join(' ');
  const tagged = /\[retry_after_seconds=(\d+)\]/.exec(text) || /retry after\s+(\d+)\s*s/i.exec(text);
  return toSeconds(tagged?.[1]);
};

/** Seconds the provider asked us to wait, or null when `error` is not a provider rate limit. */
export function providerRetryAfterSeconds(error) {
  const details = error?.details && typeof error.details === 'object' ? error.details : null;
  const code = error?.code || details?.code;
  if (code !== PROVIDER_RATE_LIMITED_CODE) return null;
  return (
    toSeconds(error?.retryAfterSeconds) ??
    toSeconds(details?.retryAfterSeconds) ??
    toSeconds(details?.details?.retry_after_seconds) ??
    toSeconds(details?.retry_after_seconds) ??
    messageRetryAfterSeconds(error?.message, error?.error, details?.error, details?.message) ??
    DEFAULT_RETRY_AFTER_SECONDS
  );
}

export const isProviderRateLimited = (error) => providerRetryAfterSeconds(error) != null;

/**
 * How long to stay away after a `retryAfterSeconds` refusal: the window plus a
 * pad, never less. It used to be capped at 6 minutes, which sent the next call
 * INTO any longer window (a 594-second one would have been asked again 234
 * seconds early). Callers bound the total with their own budgets instead.
 */
export const providerRetryWaitMs = (retryAfterSeconds) =>
  Math.max(retryAfterSeconds * 1000 + RETRY_PAD_MS, MIN_WAIT_MS);

// --- Who is waiting right now (the checkout shows it) -----------------------
const listeners = new Set();
let currentWait = null;

const publish = (wait) => {
  currentWait = wait;
  listeners.forEach((listener) => {
    try {
      listener(wait);
    } catch {
      // A listener is cosmetic; it never breaks the order.
    }
  });
};

/** Subscribe to provider waits: called with `{ label, attempt, resumeAt }` or null. Returns unsubscribe. */
export function subscribeProviderWait(listener) {
  listeners.add(listener);
  if (currentWait) listener(currentWait);
  return () => listeners.delete(listener);
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run `attempt`; while it fails with a provider rate limit, wait the window
 * out and run it again. Any other failure — and a rate limit once the budget
 * is spent — is thrown as is.
 *
 * `heartbeat` runs at the start of each wait and about once a minute during
 * it: the caller touches its order rows so the server's stuck-submission
 * sweep (claims idle for 15 minutes) never mistakes a waiting checkout for an
 * abandoned one.
 */
export async function withProviderBusyRetry(
  attempt,
  { label = null, budgetMs = DEFAULT_PROVIDER_WAIT_BUDGET_MS, heartbeat = null, sleep = defaultSleep, now = Date.now } = {},
) {
  let waitedMs = 0;
  let attempts = 0;
  while (true) {
    try {
      const result = await attempt();
      if (attempts) publish(null);
      return result;
    } catch (error) {
      const retryAfter = providerRetryAfterSeconds(error);
      if (retryAfter == null) {
        if (attempts) publish(null);
        throw error;
      }
      const waitMs = providerRetryWaitMs(retryAfter);
      if (waitedMs + waitMs > budgetMs) {
        publish(null);
        throw error;
      }
      attempts += 1;
      console.warn(
        `[provider-busy] ${label || 'request'} rate limited by the provider — waiting ${Math.ceil(waitMs / 1000)}s before attempt ${attempts + 1}`,
      );
      publish({ label, attempt: attempts, resumeAt: now() + waitMs });
      let remaining = waitMs;
      while (remaining > 0) {
        if (heartbeat) {
          try {
            await heartbeat();
          } catch (heartbeatError) {
            console.warn('[provider-busy] heartbeat failed', heartbeatError?.message || heartbeatError);
          }
        }
        const slice = Math.min(remaining, HEARTBEAT_EVERY_MS);
        await sleep(slice);
        remaining -= slice;
      }
      waitedMs += waitMs;
    }
  }
}
